import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  ColumnDef,
  PaginationState,
  Row,
  RowModel,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useEffect, useState, useCallback } from "react";
import {
  Link as LinkDom,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { useDebounce } from "use-debounce";
import * as Sentry from "@sentry/react";
import { LoadingState } from "@/components/brand";
import { ClientData } from "../types/tables-data";
import { ClientState } from "../redux/clients/clientsSlice";
import { CaseActions } from "./CaseActions";
import CustomDropdown from "./common/CustomSelectDropDown";
import CustomSkeleton from "./common/CustomSkeleton";
import { CasesAPI } from "../api/cases";
import { Case, CaseStatus } from "../api/types/case";
import { StatusTag } from "./common/StatusTag";
import { caseWorkspacePath, NEW_CASE_PATH } from "../features/caseIde/routes";

export type DataTableProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<Data, unknown>[];
  isSortable?: boolean;
  sortBy?: string;
  isLoading?: boolean;
  shouldFetchCaseStatus?: boolean;
};

interface RowLoadingState {
  [key: string]: boolean;
}

export function DataTable<Data extends ClientData>({
  data,
  columns,
  isSortable = false,
  sortBy,
  isLoading = false,
  shouldFetchCaseStatus = false
}: DataTableProps<Data>) {
  const params = useParams();
  const [sorting, setSorting] = useState<SortingState>([
    { id: sortBy ?? "createdDate", desc: true }
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Number(localStorage.getItem("dataTablePageSize")) || 10
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Use this state to hide the table temporary to optimize performance
  const [filteringData, setFilteringData] = useState(false);
  const [rowLoadingStates, setRowLoadingStates] = useState<RowLoadingState>({});
  const [caseStatuses, setCaseStatuses] = useState<
    Record<string, CaseStatus | null>
  >({});
  const [tableData, setTableData] = useState<Data[]>(data);

  // Update tableData when data prop changes
  useEffect(() => {
    setTableData(data);
    // Reset case statuses when data changes
    setCaseStatuses({});
    setRowLoadingStates({});
  }, [data]);

  // This will run the inner globalFilter
  const [filter, setFilter] = useState("");
  const [filterInput, setFilterInput] = useState("");
  const [debouncedFilter] = useDebounce(filterInput, 300);

  const fetchCaseStatuses = useCallback(
    async (visibleRows: Row<Data>[]) => {
      if (!shouldFetchCaseStatus || !data.length) return;

      const visibleIds = visibleRows.map((row) => row.original.id);
      if (visibleIds.length === 0) return;

      // Only fetch for rows we don't have status for yet (including null statuses)
      const idsToFetch = visibleIds.filter(
        (id: string) => !Object.hasOwn(caseStatuses, id)
      );
      if (idsToFetch.length === 0) return;

      try {
        // Mark only the rows we need to fetch as loading
        setRowLoadingStates((prev) => {
          const newState = { ...prev };
          idsToFetch.forEach((id: string) => {
            newState[id] = true;
          });
          return newState;
        });

        const cases = await CasesAPI.getByIndividualIds(idsToFetch);

        // Update case statuses without modifying table data
        setCaseStatuses((prev) => {
          const newStatuses = { ...prev };
          // First set all fetched IDs to null (no case found)
          idsToFetch.forEach((id) => {
            newStatuses[id] = null;
          });
          // Then update the ones that have actual cases
          cases.forEach((caseData) => {
            newStatuses[caseData.individual_id] = caseData.status;
          });
          return newStatuses;
        });
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        // Clear loading states only for the IDs we fetched
        setRowLoadingStates((prev) => {
          const newState = { ...prev };
          idsToFetch.forEach((id: string) => {
            newState[id] = false;
          });
          return newState;
        });
      }
    },
    [shouldFetchCaseStatus, data.length, caseStatuses]
  );

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      ...(isSortable && { sorting }),
      globalFilter: filter,
      pagination,
      columnVisibility: {
        email: false,
        id: false
      }
    },
    onGlobalFilterChange: setFilter
  });

  useEffect(() => {
    setFilter(debouncedFilter);
    setFilteringData(false);
  }, [debouncedFilter]);

  // Fetch case statuses when visible rows change or when data changes
  useEffect(() => {
    if (!isLoading && table.getRowModel().rows.length > 0) {
      fetchCaseStatuses(table.getRowModel().rows);
    }
  }, [table.getRowModel().rows, fetchCaseStatuses, isLoading]);

  // Reset data when navigating back
  useEffect(() => {
    if (location.state?.from === "/cases") {
      setSorting([]);
      setFilter("");
      setCaseStatuses({});
      setRowLoadingStates({});
    }
  }, [location.state]);

  const { pageIndex } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const renderPageButton = (
    label: number | string,
    { active = false, onClick }: { active?: boolean; onClick?: () => void } = {}
  ) => (
    <Button
      variant={active ? "primary" : "ghost"}
      size="icon-sm"
      className={cn("rounded-full", !onClick && "pointer-events-none")}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  return (
    <div>
      <div className="flex items-center gap-2 px-8 py-5">
        <div className="relative w-full max-w-1/2">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            className="pl-7"
            type="search"
            placeholder="Search..."
            value={filterInput}
            onChange={(e) => {
              setFilterInput(e.target.value);
              setFilteringData(true);
            }}
          />
        </div>
        <div className="flex-1" />
        <span className="text-sm text-fg-3">Viewing {data.length} cases</span>
        <Button asChild variant="primary">
          <LinkDom to={NEW_CASE_PATH}>New Case</LinkDom>
        </Button>
      </div>
      <div className="max-h-[60vh] overflow-x-auto overflow-y-scroll">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-surface">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { meta } = header.column.columnDef;
                  return (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={cn(
                        meta?.isNumeric && "text-right",
                        headerGroup.headers.indexOf(header) === 0 && "pl-8"
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() &&
                          (header.column.getIsSorted() === "desc" ? (
                            <ArrowDown
                              className="size-3"
                              aria-label="sorted descending"
                            />
                          ) : (
                            <ArrowUp
                              className="size-3"
                              aria-label="sorted ascending"
                            />
                          ))}
                      </span>
                    </TableHead>
                  );
                })}
                <TableHead />
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {filteringData ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="text-center">
                  <LoadingState className="w-full py-12" label="Loading rows" />
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell className="pl-8">
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="150px"
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="80px"
                    />
                  </TableCell>
                  <TableCell>
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="200px"
                    />
                  </TableCell>
                  <TableCell>
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="100px"
                    />
                  </TableCell>
                  <TableCell>
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="100px"
                    />
                  </TableCell>
                  <TableCell>
                    <CustomSkeleton
                      variant="single"
                      height="24px"
                      width="20px"
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="cursor-default hover:bg-transparent">
                <TableCell colSpan={columns.length + 1} className="p-0">
                  <EmptyState
                    title="No Active Cases Found"
                    description={'Click "New Case" to add the first client'}
                  />
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => {
                    if (!params.caseID && location.pathname === "/cases") {
                      // Legacy cases table: no client link on the row, so
                      // use the bare case route; the shell canonicalises.
                      const path = caseWorkspacePath({
                        individualId: row.original.id
                      });
                      // Ensure we're setting the state to track where we navigated from
                      navigate(path, { state: { from: "/cases" } });
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const { meta, id } = cell.column.columnDef;

                    // If this is the status cell and we're loading or have status
                    if (id === "caseStatus" && shouldFetchCaseStatus) {
                      if (
                        rowLoadingStates[row.original.id] ||
                        !Object.hasOwn(caseStatuses, row.original.id)
                      ) {
                        return (
                          <TableCell key={cell.id}>
                            <CustomSkeleton
                              variant="single"
                              height="24px"
                              width="100px"
                            />
                          </TableCell>
                        );
                      }
                      const status = caseStatuses[row.original.id];
                      return (
                        <TableCell key={cell.id}>
                          {status ? (
                            <StatusTag status={status} />
                          ) : (
                            <span className="text-sm">-</span>
                          )}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          meta?.isNumeric && "text-right tabular-nums",
                          id === "visa"
                            ? "whitespace-nowrap"
                            : "whitespace-normal",
                          row.getVisibleCells().indexOf(cell) === 0 && "pl-8"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell key="delete" className="text-center">
                    <CaseActions
                      caseId={row.original.id}
                      clientName={`${row.original.firstName} ${row.original.lastName}`}
                      individualData={{
                        id: row.original.id,
                        firstName: row.original.firstName,
                        middleName: (row.original as ClientState).middleName,
                        lastName: row.original.lastName,
                        email: row.original.email,
                        visa: row.original.visa || "",
                        employerInfo: (row.original as ClientState)
                          .employerInfo,
                        employerPetitioner: (row.original as ClientState)
                          .employerPetitioner,
                        petitionerName: (row.original as ClientState)
                          .petitionerName,
                        title: (row.original as ClientState).title,
                        pronouns: (row.original as ClientState).pronouns
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 flex items-center justify-end gap-2">
        <span className="text-base text-fg-2">Cases per page</span>
        <CustomDropdown
          options={[10, 20, 50, 75, 100]}
          casesNumber={data.length}
          currentValue={table.getState().pagination.pageSize}
          onChange={(val) => {
            const newPageSize = Number(val);
            table.setPageSize(newPageSize);
            localStorage.setItem("dataTablePageSize", String(newPageSize));
          }}
        />

        <div className="flex items-center gap-2">
          <Button
            aria-label="previous page"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ArrowLeft />
            Previous
          </Button>
          <span className="flex items-end gap-2">
            {/* Show page numbers */}
            {pageCount > 1 ? (
              <>
                {/* First Page */}
                {renderPageButton(1, {
                  active: pageIndex === 0,
                  onClick: () => table.setPageIndex(0)
                })}

                {pageIndex > 1 && renderPageButton("...")}

                {/* Current Page (if not first or last) */}
                {pageIndex > 0 &&
                  pageIndex < pageCount - 1 &&
                  renderPageButton(pageIndex + 1, {
                    active: true,
                    onClick: () => undefined
                  })}

                {pageIndex < pageCount - 2 && renderPageButton("...")}

                {/* Last Page */}
                {renderPageButton(pageCount, {
                  active: pageIndex === pageCount - 1,
                  onClick: () => table.setPageIndex(pageCount - 1)
                })}
              </>
            ) : (
              renderPageButton(1, { active: true, onClick: () => undefined })
            )}
          </span>
          <Button
            aria-label="next page"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
            <ArrowRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
